/**
 * HUK-128 — LoyaltyLion promo popup keyboard accessibility
 *
 * LoyaltyLion injects its "guest introduction" notification (the "Join The
 * Captain's Club" card) dynamically into the same-origin DOM at
 * `#loyaltylion .lion-notification`. By default it is a non-modal top-right
 * toast that: never receives focus, sits at the end of the tab order, can't be
 * closed with the keyboard (no Escape; close/CTA are <a role="button"> with no
 * href so Space doesn't activate them), and lacks an accessible name.
 *
 * This progressive enhancement keeps it a non-modal toast (per stakeholder
 * decision) but makes it fully keyboard-accessible:
 *   - gives the notification an accessible name (role="group" + aria-label)
 *   - moves focus into it when it appears
 *   - makes the close (×) and CTA respond to BOTH Enter and Space
 *   - lets Escape dismiss it (reusing LoyaltyLion's own close + persistence)
 *   - returns focus to where it was when the toast is dismissed
 * It intentionally does NOT trap focus (correct for a non-modal notification).
 *
 * Safe to run on every page: guarded, idempotent, and a no-op when LoyaltyLion
 * is absent or its markup changes.
 */
(function () {
  'use strict';

  var ROOT_ID = 'loyaltylion';
  var NOTIF_SELECTOR = '.lion-notification';
  var CLOSE_SELECTOR =
    '.lion-notification__close-button, [data-testid="notification-close-button"]';
  var CTA_SELECTOR =
    '.lion-notification__action-button, .lion-action-button';
  var ENHANCED_FLAG = 'acdcA11y'; // -> data-acdc-a11y
  var KEYABLE_FLAG = 'acdcKeyable'; // -> data-acdc-keyable

  function isEnterOrSpace(e) {
    return (
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'Spacebar' || // legacy
      e.keyCode === 13 ||
      e.keyCode === 32
    );
  }

  function isEscape(e) {
    return e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
  }

  // Make an <a role="button"> (no href) activate on Enter AND Space.
  function makeKeyboardActivatable(el) {
    if (!el || el.dataset[KEYABLE_FLAG]) return;
    el.dataset[KEYABLE_FLAG] = '1';
    el.addEventListener('keydown', function (e) {
      if (isEnterOrSpace(e)) {
        e.preventDefault(); // stop Space from scrolling the page
        el.click();
      }
    });
  }

  function restoreFocus(previous) {
    try {
      if (
        previous &&
        previous !== document.body &&
        document.contains(previous) &&
        typeof previous.focus === 'function'
      ) {
        previous.focus({ preventScroll: true });
        return;
      }
    } catch (err) {
      /* fall through */
    }
    // Fallback: send focus to main content rather than losing it entirely.
    try {
      var main =
        document.querySelector('main, #MainContent, [role="main"]') ||
        document.body;
      if (main && main.tabIndex < 0) main.setAttribute('tabindex', '-1');
      if (main) main.focus({ preventScroll: true });
    } catch (err2) {
      /* no-op */
    }
  }

  function enhance(notif) {
    if (!notif || notif.dataset[ENHANCED_FLAG]) return;
    notif.dataset[ENHANCED_FLAG] = '1';

    // 1. Accessible name (non-modal grouping; container keeps its aria-live).
    var labelSource = notif.querySelector(
      '.lion-notification__message, h1, h2, h3, [class*="title"], [class*="heading"]'
    );
    var label =
      (labelSource && labelSource.textContent.trim()) || 'Loyalty offer';
    label = label.replace(/\s+/g, ' ').slice(0, 120);
    if (!notif.getAttribute('role')) notif.setAttribute('role', 'group');
    if (!notif.getAttribute('aria-label'))
      notif.setAttribute('aria-label', label);

    // 2. Make the toast focusable and move focus into it on open.
    var previouslyFocused = document.activeElement;
    if (!notif.hasAttribute('tabindex')) notif.setAttribute('tabindex', '-1');
    setTimeout(function () {
      // Only steal focus if the notification is still present/visible.
      if (document.contains(notif) && notif.offsetParent !== null) {
        try {
          notif.focus({ preventScroll: true });
        } catch (e) {
          try {
            notif.focus();
          } catch (e2) {
            /* no-op */
          }
        }
      }
    }, 50);

    // 3. Keyboard-operable close + CTA.
    var closeBtn = notif.querySelector(CLOSE_SELECTOR);
    var cta = notif.querySelector(CTA_SELECTOR);
    if (!cta) {
      var interactive = notif.querySelectorAll('a, button');
      for (var i = 0; i < interactive.length; i++) {
        if (interactive[i] !== closeBtn) {
          cta = interactive[i];
          break;
        }
      }
    }
    makeKeyboardActivatable(closeBtn);
    if (cta && cta !== closeBtn) makeKeyboardActivatable(cta);

    // 4. Escape dismisses (reuse LoyaltyLion's own close handler + persistence).
    notif.addEventListener('keydown', function (e) {
      if (isEscape(e)) {
        e.preventDefault();
        if (closeBtn) {
          closeBtn.click();
        }
        restoreFocus(previouslyFocused);
      }
    });

    // 5. Return focus whenever the toast is dismissed (mouse or keyboard).
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        // Defer so LoyaltyLion can remove the node first.
        setTimeout(function () {
          restoreFocus(previouslyFocused);
        }, 0);
      });
    }
  }

  function scanWithin(node) {
    if (!node || !node.querySelectorAll) return;
    var notifs = node.querySelectorAll(NOTIF_SELECTOR);
    for (var i = 0; i < notifs.length; i++) {
      if (notifs[i].closest('#' + ROOT_ID)) enhance(notifs[i]);
    }
  }

  function init() {
    var root = document.getElementById(ROOT_ID);
    if (root) scanWithin(root);

    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var a = 0; a < added.length; a++) {
          var node = added[a];
          if (node.nodeType !== 1) continue;
          if (
            node.matches &&
            node.matches(NOTIF_SELECTOR) &&
            node.closest('#' + ROOT_ID)
          ) {
            enhance(node);
          } else if (node.querySelector) {
            scanWithin(node);
          }
        }
      }
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
